import { useState, useEffect } from "react";
import { walletApi } from "@/lib/api";
import { Wallet, ToggleRight, ToggleLeft, ArrowLeft, ArrowUp, ArrowDown, MagnifyingGlass } from "@phosphor-icons/react";
import { format } from "date-fns";

const SOURCE_LABEL = {
  mpesa: "M-Pesa top-up",
  auto_pay: "Auto-pay",
  manual_pay: "Manual pay",
  sweep: "Sweep arrears",
  admin: "Adjustment",
};

export default function WalletsAdminTab() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);  // { userId, name, balance, txns, autoPayEnabled }

  useEffect(() => {
    walletApi.adminList()
      .then(({ data }) => { setRows(data?.wallets || []); setTotal(data?.totalLiability || 0); })
      .finally(() => setLoading(false));
  }, []);

  const openMember = async (row) => {
    try {
      const { data } = await walletApi.adminMemberTxns(row.userId, 500);
      setSelected({ ...row, txns: data?.transactions || [], balance: data?.balance ?? row.balance, autoPayEnabled: data?.autoPayEnabled ?? row.autoPayEnabled });
    } catch {
      setSelected({ ...row, txns: [] });
    }
  };

  const filtered = search.trim()
    ? rows.filter(r =>
        r.name?.toLowerCase().includes(search.toLowerCase()) ||
        r.memberNumber?.toLowerCase().includes(search.toLowerCase()) ||
        r.phone?.includes(search)
      )
    : rows;

  // ── Member detail view ───────────────────────────────────────────────────
  if (selected) {
    return (
      <div className="space-y-4" data-testid="wallet-admin-detail">
        <button onClick={() => setSelected(null)} className="flex items-center gap-1 text-sm text-primary"><ArrowLeft className="w-4 h-4" /> Back to wallets list</button>
        <div className="bg-gradient-to-br from-emerald-50 to-emerald-100/60 border border-emerald-200 rounded-xl p-5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-emerald-600 text-white flex items-center justify-center">
              <Wallet className="w-5 h-5" weight="fill" />
            </div>
            <div>
              <p className="font-semibold">{selected.name}</p>
              <p className="text-xs text-muted-foreground">{selected.memberNumber} · {selected.phone}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xs text-muted-foreground">Balance</p>
            <p className="font-heading text-2xl font-bold text-emerald-700">KES {Math.round(selected.balance).toLocaleString()}</p>
            <p className="text-[11px] text-muted-foreground flex items-center gap-1 justify-end mt-0.5">
              Auto-pay: {selected.autoPayEnabled ? <ToggleRight className="w-4 h-4 text-emerald-600" weight="fill" /> : <ToggleLeft className="w-4 h-4" />}
              <span>{selected.autoPayEnabled ? "ON" : "OFF"}</span>
            </p>
          </div>
        </div>

        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-border bg-secondary/30">
            <p className="font-heading font-semibold text-sm">Transactions ({selected.txns.length})</p>
          </div>
          {selected.txns.length === 0 ? (
            <div className="px-5 py-8 text-center text-sm text-muted-foreground">No transactions yet.</div>
          ) : (
            <div className="divide-y divide-border">
              {selected.txns.map(t => {
                const credit = t.type === "topup";
                return (
                  <div key={t.id} className="px-5 py-3 flex items-start justify-between gap-3">
                    <div className="flex items-start gap-2 min-w-0">
                      <div className={`mt-0.5 w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${credit ? "bg-emerald-100 text-emerald-700" : "bg-orange-100 text-orange-700"}`}>
                        {credit ? <ArrowDown className="w-3.5 h-3.5" weight="bold" /> : <ArrowUp className="w-3.5 h-3.5" weight="bold" />}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{t.note || SOURCE_LABEL[t.source] || t.type}</p>
                        <p className="text-[11px] text-muted-foreground">
                          {SOURCE_LABEL[t.source] || t.source}
                          {t.reference && <> · <span className="font-mono">{t.reference}</span></>}
                          {t.createdAt && <> · {format(new Date(t.createdAt), "dd MMM yyyy HH:mm")}</>}
                        </p>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className={`text-sm font-bold font-body ${credit ? "text-emerald-700" : "text-orange-700"}`}>{credit ? "+" : "−"} KES {Math.round(t.amount).toLocaleString()}</p>
                      <p className="text-[11px] text-muted-foreground">Balance: KES {Math.round(t.balanceAfter || 0).toLocaleString()}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── List view ───────────────────────────────────────────────────────────
  return (
    <div className="space-y-4" data-testid="wallet-admin-list">
      <div className="bg-gradient-to-br from-teal-50 to-teal-100/60 border border-teal-200 rounded-xl p-5 flex items-center justify-between">
        <div>
          <p className="text-xs text-muted-foreground">Wallet liabilities (held member funds)</p>
          <p className="font-heading text-2xl font-bold text-teal-700">KES {Math.round(total).toLocaleString()}</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">{rows.filter(r => r.balance > 0).length} of {rows.length} wallets funded</p>
        </div>
        <Wallet className="w-12 h-12 text-teal-600/40" weight="fill" />
      </div>

      <div className="relative">
        <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, UKC, or phone…" data-testid="wallet-admin-search" className="w-full pl-9 pr-3 py-2 text-sm bg-card border border-input rounded-xl focus:outline-none focus:ring-2 focus:ring-ring" />
      </div>

      <div className="bg-card border border-border rounded-xl overflow-hidden">
        {loading ? (
          <div className="px-5 py-8 text-center text-sm text-muted-foreground">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="px-5 py-8 text-center text-sm text-muted-foreground">No wallets match.</div>
        ) : (
          <div className="divide-y divide-border">
            {filtered.map(r => (
              <button key={r.userId} onClick={() => openMember(r)} data-testid={`wallet-row-${r.userId}`} className="w-full px-5 py-3 flex items-center justify-between gap-3 hover:bg-secondary/50 transition-colors text-left">
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{r.name}</p>
                  <p className="text-[11px] text-muted-foreground truncate">{r.memberNumber} · {r.phone} · {r.status}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className={`text-sm font-bold font-body ${r.balance > 0 ? "text-emerald-700" : "text-muted-foreground"}`}>KES {Math.round(r.balance).toLocaleString()}</p>
                  <p className="text-[11px] text-muted-foreground flex items-center gap-1 justify-end">
                    {r.autoPayEnabled
                      ? <><ToggleRight className="w-3.5 h-3.5 text-emerald-600" weight="fill" /> Auto-pay</>
                      : <><ToggleLeft className="w-3.5 h-3.5" /> Manual</>
                    }
                  </p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
